import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Film, Pause, Play, RotateCcw, X } from 'lucide-react';
import type { ReplayNode } from '@dxp/schema';
import { getReplayChunk, getSessionReplay, qk } from '../api.js';
import EmptyState, { ErrorState } from './EmptyState.js';
import { LoadingBlock } from './Spinner.js';
import {
  buildTimeline,
  clamp,
  compressGaps,
  formatClock,
  mapToPlayTime,
  markers as buildMarkers,
  netRows as buildNetRows,
  normalizeChunkBody,
  type MarkerKind,
  type NetRow,
  type PlaybackEvent,
  type ReplayEventLike,
  type SessionEventLike,
} from './replay-engine.js';

/**
 * §22.7 — Session replay oynatıcısı.
 *
 * Zamanlama/işaretçi mantığı `replay-engine.ts`'tedir (saf, testli); burada yalnızca
 * "kaydı ekrana koymak" vardır: chunk gövdelerinin indirilmesi, snapshot ağacının
 * iframe DOM'una çevrilmesi, mutation'ların düğüm kimliğiyle uygulanması ve kontroller.
 *
 * Kabuk (modal katman, ESC ile kapatma) çağıran sayfanın işidir — bileşen kendisine
 * verilen alanı doldurur.
 */

export interface ReplayPlayerProps {
  pid: string;
  sid: string;
  /**
   * §10 events ucundan gelen oturum olayları — timeline işaretçilerinin ve sağ
   * paneldeki senkron listenin KAYNAĞI budur (kayıt akışı değil, §22.7).
   */
  sessionEvents?: readonly SessionEventLike[];
  /**
   * §22.4 — kaydedilen sayfanın origin'i (`projects.domain`'den, sonda `/` ile).
   * Snapshot'a `<base href>` olarak enjekte edilir; göreli adresler PANELİN
   * origin'ine değil, müşterinin sitesine çözülür. Gerekçe: `injectBase`.
   *
   * Verilmezse enjeksiyon yapılmaz — oynatıcı yine çalışır, yalnız göreli
   * varlıklar yüklenmez (proje domaini tanımsızken zaten yükleyecek adres yoktur).
   */
  baseOrigin?: string | null;
  onClose: () => void;
}

/** §22.7 — "hız 1×/2×/4×". */
const SPEEDS = [1, 2, 4] as const;
type Speed = (typeof SPEEDS)[number];

/** §22.4 — maskeleme karakteri; input olayı yalnız uzunluk taşır, değer taşımaz. */
const MASK_CHAR = '▪';
/** Bozuk `len` alanının belleği şişirmemesi için üst sınır. */
const MAX_MASK_LEN = 512;

const ELEMENT_NODE = 1;
const SVG_NS = 'http://www.w3.org/2000/svg';

/** Kayıt yoksa varsayılan sahne ölçüsü — ilk snapshot gelene kadar boş çerçeve. */
const DEFAULT_VIEWPORT = { w: 1280, h: 800 };

/* -------------------------------------------------------------------------- */
/* Olay alanı okuyucuları                                                       */
/* -------------------------------------------------------------------------- */

/*
 * Chunk gövdesi ağdan gelir ve `[key: string]: unknown` sözleşmesindedir; her alan
 * okuması daraltılır. Amaç savunmacılık değil TİP DOĞRULUĞU: eksik/bozuk bir alan
 * yüzünden oynatıcı çökmemeli, o olay atlanmalıdır (§22.5 kayıp-tolerans).
 */

function num(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function str(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}

function record(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === 'object' ? (value as Record<string, unknown>) : null;
}

/* -------------------------------------------------------------------------- */
/* Snapshot → gerçek DOM (§22.4 ağacı)                                          */
/* -------------------------------------------------------------------------- */

/** Düğüm kimliği (`n`) → iframe içindeki gerçek Node. Mutation hedefleri buradan bulunur. */
type NodeMap = Map<number, Node>;

function register(nodes: NodeMap, id: unknown, node: Node): void {
  const key = num(id);
  if (key !== null) nodes.set(key, node);
}

/**
 * Belgeyi boşaltır. `document.write` yerine `documentElement` değişimi tercih edildi:
 * sandbox'lı çerçevede write() akışı tarayıcıya göre farklı davranabiliyor, replaceChild
 * ise saf DOM işlemidir ve senkron biter.
 */
function clearDocument(doc: Document): void {
  const html = doc.createElement('html');
  html.appendChild(doc.createElement('head'));
  html.appendChild(doc.createElement('body'));
  const current = doc.documentElement;
  if (current === null) doc.appendChild(html);
  else doc.replaceChild(html, current);
}

/** Bir düğümün ad alanı — SVG alt ağacında oluşturulan çocuklar da SVG olmalı. */
function namespaceOf(node: Node): string | null {
  return node.nodeType === ELEMENT_NODE ? (node as Element).namespaceURI : null;
}

/**
 * §22.4 serileştirilmiş düğümü gerçek DOM'a çevirir ve kimliğini haritaya yazar.
 * `null` dönerse düğüm kurulmaz (script, geçersiz etiket) — üst ağaç bozulmaz.
 */
/** §36.3 — ağaç bağlandıktan SONRA doldurulacak same-origin çerçeve. */
interface PendingFrame {
  el: HTMLIFrameElement;
  root: ReplayNode;
}

/** §36.3 — cross-origin çerçevenin yerine çizilen gri desenli alan. */
const XORIGIN_STYLE =
  'background:repeating-linear-gradient(45deg,#f3f4f6,#f3f4f6 6px,#e5e7eb 6px,#e5e7eb 12px);' +
  'border:1px dashed #d1d5db;';

function createNode(
  doc: Document,
  node: ReplayNode,
  nodes: NodeMap,
  ns: string | null,
  frames: PendingFrame[],
): Node | null {
  const tag = str(node.tag);

  // 'tag' yoksa metin düğümüdür (§22.4 — metin varsayılan maskelidir, olduğu gibi basılır).
  if (tag === null) {
    const text = doc.createTextNode(str(node.text) ?? '');
    register(nodes, node.n, text);
    return text;
  }

  /*
   * §22.7 KARARI gereği kaydedilen sitenin JS'i panelde ASLA çalışmaz. Sandbox zaten
   * script'i çalıştırmaz ve §22.4 gövdeyi atar; yine de <script> düğümünü hiç kurmuyoruz
   * — savunma tek katmana bırakılmaz.
   */
  const lower = tag.toLowerCase();
  if (lower === 'script') return null;

  const childNs = lower === 'svg' ? SVG_NS : ns;
  let element: Element;
  try {
    element = childNs === null ? doc.createElement(lower) : doc.createElementNS(childNs, lower);
  } catch {
    return null; // geçersiz etiket adı — bu dal atlanır
  }

  const attrs = node.attrs;
  if (attrs !== undefined && attrs !== null) {
    for (const [name, value] of Object.entries(attrs)) {
      if (typeof value !== 'string') continue;
      /*
       * §36.3 — çerçevenin `src`'si PANELDE ASLA KURULMAZ. Kurulsaydı oynatıcı,
       * kaydı izleyen kişinin tarayıcısından üçüncü tarafa GERÇEK bir istek atardı
       * (izleyiciyi izlenen sayfaya bağlar, reklam/analitik çerezi ateşler).
       * İçerik zaten ya `frame` alanında kayıtlıdır ya da yer tutucudur.
       */
      if (lower === 'iframe' && name === 'src') continue;
      try {
        element.setAttribute(name, value);
      } catch {
        /* geçersiz attribute adı — atla, kayıt sürsün */
      }
    }
  }

  register(nodes, node.n, element);

  const children = node.children;
  if (Array.isArray(children)) {
    for (const child of children) {
      const built = record(child) === null ? null : createNode(doc, child, nodes, childNs, frames);
      if (built !== null) element.appendChild(built);
    }
  }

  if (lower === 'canvas') applyCanvasImage(element, node.img, node.ph);

  mountShadow(doc, node, element, nodes, childNs, frames);

  if (lower === 'iframe') {
    const frame = record(node.frame);
    if (frame === null) {
      // §36.3 — cross-origin: içerik OKUNAMADI, gri desenli alan çizilir.
      element.setAttribute('style', (attrs?.['style'] ?? '') + XORIGIN_STYLE);
    } else {
      // Belgesi ancak DOM'a bağlandıktan sonra erişilebilir → sonraya bırakılır.
      frames.push({ el: element as HTMLIFrameElement, root: node.frame as ReplayNode });
    }
  }

  return element;
}

/**
 * §36.2 — canvas karesini öğeye basar.
 *
 * Görüntüyü `drawImage` ile çizmek `Image.onload` bekler; geri sarma sırasında kareler
 * yanlış SIRADA inebilir ve oynatıcı geçmişteki bir kareyi gösterir. `background-image`
 * SENKRONDUR ve zaman ekseniyle her zaman tutarlıdır.
 *
 * `data:` DIŞINDA bir URI KABUL EDİLMEZ: kayıt gövdesi, paneli açan kişinin
 * tarayıcısından uzaktaki bir sunucuya görsel isteği yaptıramaz (§22.7).
 */
function applyCanvasImage(element: Element, img: unknown, ph: unknown): void {
  const base = element.getAttribute('style') ?? '';
  if (typeof img === 'string' && img.slice(0, 5).toLowerCase() === 'data:') {
    element.setAttribute(
      'style',
      `${base}background-image:url("${img.replace(/"/g, '%22')}");background-size:100% 100%;`,
    );
    return;
  }
  // §36.2 — kirlenmiş ya da tavanı aşan canvas: gri desenli yer tutucu.
  if (ph === 'c') element.setAttribute('style', base + XORIGIN_STYLE);
}

/**
 * §36.3 — bağlanmış çerçevelerin belgelerini doldurur.
 *
 * `contentDocument` yalnız öğe belgeye EKLENDİKTEN sonra vardır; bu yüzden kurulum
 * iki aşamalıdır. İç içe çerçeveler yeni bekleyenler doğurabilir — döngü sınırlıdır
 * (bozuk/döngüsel kayıt paneli kilitlememeli).
 */
const MAX_FRAME_DEPTH = 4;

function flushFrames(pending: PendingFrame[], nodes: NodeMap, baseHref: string | null): void {
  let queue = pending;
  for (let depth = 0; depth < MAX_FRAME_DEPTH && queue.length > 0; depth += 1) {
    const next: PendingFrame[] = [];
    for (const item of queue) {
      let sub: Document | null = null;
      try {
        sub = item.el.contentDocument;
      } catch {
        sub = null;
      }
      // Erişilemiyorsa (sandbox politikası) yer tutucuya düşülür — boş beyaz kutu değil.
      if (sub === null) {
        item.el.setAttribute('style', (item.el.getAttribute('style') ?? '') + XORIGIN_STYLE);
        continue;
      }
      mountTree(sub, item.root, nodes, next, baseHref);
    }
    queue = next;
  }
}

/**
 * §36.4 — kaydedilen açık shadow root'u panelde GERÇEK shadow root olarak kurar.
 *
 * Çocukları doğrudan host'a eklemek daha basit olurdu ama YANLIŞ görüntü verirdi:
 * shadow içindeki `<style>` kapsamlıdır; light DOM'a taşındığında sayfanın tamamını
 * boyar ve kayıt, kullanıcının gördüğünden farklı görünür.
 *
 * `attachShadow` bazı etiketlerde (ör. `<input>`) yasaktır ve fırlatır; o durumda
 * shadow atlanır — ağacın geri kalanı kurulmaya devam eder.
 */
function mountShadow(
  doc: Document,
  node: ReplayNode,
  element: Element,
  nodes: NodeMap,
  ns: string | null,
  frames: PendingFrame[],
): void {
  const shadow = record(node.shadow);
  if (shadow === null) return;
  const kids = shadow['children'];
  if (!Array.isArray(kids)) return;

  let root: ShadowRoot;
  try {
    root = element.attachShadow({ mode: 'open' });
  } catch {
    return;
  }
  // Kökün KENDİ kimliği: shadow içi childList mutation'ının hedefi budur (§36.4).
  register(nodes, shadow['n'], root);

  for (const child of kids) {
    const built =
      record(child) === null ? null : createNode(doc, child as ReplayNode, nodes, ns, frames);
    if (built !== null) root.appendChild(built);
  }
}

/**
 * §22.4 — kaydedilen sayfanın origin'ini `<base href>` ile geri verir.
 *
 * NEDEN GEREKLİ: snapshot PANELİN origin'indeki bir iframe'e kurulur. Kayıttaki
 * göreli adresler (`/assets/app.css`, `/img/a.webp`) o çerçevede panele çözülür;
 * panel bilinmeyen yola SPA fallback'i (`index.html`) döndürdüğü için tarayıcı
 * `text/html` görür ve strict MIME check ile reddeder — kayıt stilsiz görünür.
 *
 * NEDEN TEK BAŞINA YETMEZ: üçüncü taraf origin'leri (fonts, CDN) `<base>` ile
 * kurtarılamaz, onlar için origin kayıtta korunmalıdır (SDK `serializeAttrValue`,
 * `maskAbsUrl`). Bu enjeksiyon o düzeltmeden ÖNCE alınmış kayıtları da okunur
 * kılar: eski kayıtlarda origin silinmiştir ve geri gelmesinin başka yolu yoktur.
 *
 * ZAMANLAMA KRİTİK: ağaç belgeye BAĞLANMADAN önce çağrılmalıdır. Bağlandıktan
 * sonra eklenen `<base>` işe yaramaz; `<link>`/`<img>` yüklemeleri çoktan yanlış
 * adrese çıkmış olur.
 */
function injectBase(htmlEl: Element, doc: Document, baseHref: string | null): void {
  if (baseHref === null || baseHref === '') return;

  let head: Element | null = null;
  const kids = htmlEl.childNodes;
  for (let i = 0; i < kids.length; i += 1) {
    const child = kids[i];
    if (child === undefined || child === null) continue;
    if (child.nodeType !== ELEMENT_NODE) continue;
    if ((child as Element).tagName.toLowerCase() === 'head') {
      head = child as Element;
      break;
    }
  }
  if (head === null) {
    head = doc.createElement('head');
    htmlEl.insertBefore(head, htmlEl.firstChild);
  }

  // İLK çocuk: HTML spec'inde birden çok `<base>` varsa ilki kazanır; kayıttaki
  // özgün `<base>` (varsa) bizimkini EZMEMELİ, origin'i o zaten taşımıyor.
  const base = doc.createElement('base');
  base.setAttribute('href', baseHref);
  head.insertBefore(base, head.firstChild);
}

/** Snapshot olayının ağacını çerçeveye kurar; kimlik haritası sıfırlanır. */
function mountSnapshot(
  doc: Document,
  root: ReplayNode,
  nodes: NodeMap,
  baseHref: string | null,
): void {
  nodes.clear();
  const frames: PendingFrame[] = [];
  mountTree(doc, root, nodes, frames, baseHref);
  // §36.3 — çerçeveler ancak ağaç belgeye bağlandıktan sonra doldurulabilir.
  flushFrames(frames, nodes, baseHref);
}

/** Bir ağacı verilen belgeye kurar (kimlik haritasını SIFIRLAMADAN — iç çerçeveler paylaşır). */
function mountTree(
  doc: Document,
  root: ReplayNode,
  nodes: NodeMap,
  frames: PendingFrame[],
  baseHref: string | null,
): void {
  const built = createNode(doc, root, nodes, null, frames);
  if (built === null) {
    clearDocument(doc);
    return;
  }

  const isHtml =
    built.nodeType === ELEMENT_NODE && (built as Element).tagName.toLowerCase() === 'html';
  if (isHtml && doc.documentElement !== null) {
    injectBase(built as Element, doc, baseHref);
    doc.replaceChild(built, doc.documentElement);
    return;
  }

  clearDocument(doc);
  // `<html>` kökü yoksa `<base>` belgenin KENDİ head'ine girer; yine ekleme
  // öncesidir, dolayısıyla `built` içindeki adresler doğru çözülür.
  const shell = doc.documentElement;
  if (shell !== null) injectBase(shell, doc, baseHref);
  doc.body?.appendChild(built);
}

/* -------------------------------------------------------------------------- */
/* Mutation uygulaması (§22.4 kind kırılımı)                                    */
/* -------------------------------------------------------------------------- */

function applyChildList(
  doc: Document,
  ev: ReplayEventLike,
  nodes: NodeMap,
  baseHref: string | null,
): void {
  const parentId = num(ev['n']);
  if (parentId === null) return;
  const parent = nodes.get(parentId);
  // Bilinmeyen kimlik → olayı ATLA: kayıt eksik başlamış olabilir (buffer modu), çökme yok.
  if (parent === undefined) return;

  /*
   * Önce silme: aynı olayda bir düğüm taşınmış olabilir (remove + add). Ekleme önce
   * yapılsaydı, taşınan düğümün kimliği haritadan yeni hâliyle silinirdi.
   */
  const removed = ev['removed'];
  if (Array.isArray(removed)) {
    for (const raw of removed) {
      const id = num(raw);
      if (id === null) continue;
      const target = nodes.get(id);
      target?.parentNode?.removeChild(target);
      nodes.delete(id);
    }
  }

  const added = ev['added'];
  if (!Array.isArray(added) || added.length === 0) return;

  /*
   * `prev` = eklenenlerin ARDINA geleceği kardeş. Yoksa başa eklenir (§22.7 sözleşmesi).
   * `prev` verilmiş ama düğüm bilinmiyorsa sona ekliyoruz: konum tahmin etmek yerine
   * içeriği görünür kılmak, teşhis için daha az zararlı.
   */
  const prevId = num(ev['prev']);
  let anchor: Node | null;
  if (prevId === null) {
    anchor = parent.firstChild;
  } else {
    const prev = nodes.get(prevId);
    anchor = prev !== undefined && prev.parentNode === parent ? prev.nextSibling : null;
  }

  const ns = namespaceOf(parent);
  /*
   * §36.3 — mutation ile SONRADAN eklenen bir alt ağaç da çerçeve içerebilir.
   * Bunlar da ancak DOM'a bağlandıktan sonra doldurulabilir → aynı iki aşamalı yol.
   */
  const frames: PendingFrame[] = [];
  for (const child of added) {
    if (record(child) === null) continue;
    const built = createNode(doc, child as ReplayNode, nodes, ns, frames);
    if (built === null) continue;
    // Aynı `anchor` ile eklemek kayıt sırasını korur (hepsi anchor'dan önce, sırayla).
    parent.insertBefore(built, anchor);
  }
  flushFrames(frames, nodes, baseHref);
}

function applyAttributes(ev: ReplayEventLike, nodes: NodeMap): void {
  const id = num(ev['n']);
  if (id === null) return;
  const target = nodes.get(id);
  if (target === undefined || target.nodeType !== ELEMENT_NODE) return;
  const name = str(ev['name']);
  if (name === null || name === '') return;

  const value = ev['value'];
  try {
    // §22.4 — value null ise attribute SİLİNMİŞTİR; boş string ile karıştırılmamalı.
    if (value === null) (target as Element).removeAttribute(name);
    else if (typeof value === 'string') (target as Element).setAttribute(name, value);
  } catch {
    /* geçersiz attribute adı — atla */
  }
}

function applyCharacterData(ev: ReplayEventLike, nodes: NodeMap): void {
  const id = num(ev['n']);
  if (id === null) return;
  const target = nodes.get(id);
  if (target === undefined) return;
  target.nodeValue = str(ev['text']) ?? '';
}

/* -------------------------------------------------------------------------- */
/* Olay uygulayıcı                                                              */
/* -------------------------------------------------------------------------- */

/** DOM dışına düşen etkiler (imleç, ölçü, rota) bileşene geri bildirilir. */
interface StageHooks {
  onViewport: (w: number, h: number) => void;
  onPointer: (x: number, y: number) => void;
  onClick: (x: number, y: number) => void;
  onPage: (path: string) => void;
}

function applyEvent(
  doc: Document,
  ev: ReplayEventLike,
  nodes: NodeMap,
  hooks: StageHooks,
  baseHref: string | null,
): void {
  switch (ev.t) {
    case 'snapshot': {
      const root = record(ev['doc']);
      if (root !== null) mountSnapshot(doc, root as ReplayNode, nodes, baseHref);
      const w = num(ev['w']);
      const h = num(ev['h']);
      if (w !== null && h !== null && w > 0 && h > 0) hooks.onViewport(w, h);
      break;
    }

    case 'mutation': {
      switch (str(ev['kind'])) {
        case 'childList':
          applyChildList(doc, ev, nodes, baseHref);
          break;
        case 'attributes':
          applyAttributes(ev, nodes);
          break;
        case 'characterData':
          applyCharacterData(ev, nodes);
          break;
        default:
          break; // bilinmeyen kind → atla
      }
      break;
    }

    // §36.2 — canvas'a çizim DOM mutation'ı üretmez; kare ayrı olayla gelir.
    case 'canvas': {
      const id = num(ev['n']);
      if (id === null) break;
      const target = nodes.get(id);
      if (target === undefined || target.nodeType !== ELEMENT_NODE) break;
      applyCanvasImage(target as Element, ev['img'], 'c');
      break;
    }

    case 'input': {
      const id = num(ev['n']);
      const len = num(ev['len']);
      if (id === null || len === null) break;
      const target = nodes.get(id);
      if (target === undefined || target.nodeType !== ELEMENT_NODE) break;
      // §22.4 — değer kaydedilmez; yazma hissini maskeli karakterle canlandırıyoruz.
      const field = target as Partial<HTMLInputElement>;
      if (typeof field.value === 'string') {
        field.value = MASK_CHAR.repeat(clamp(Math.round(len), 0, MAX_MASK_LEN));
      }
      break;
    }

    case 'scroll': {
      const x = num(ev['x']);
      const y = num(ev['y']);
      if (x === null || y === null) break;
      /*
       * §22.7 — kaydırma SAF DOM ile yapılır, `defaultView.scrollTo` ile DEĞİL.
       *
       * Çerçevede `allow-scripts` YOKTUR (bilinçli); `window` nesnesine uzanmak
       * tarayıcıyı o çerçeve için script bağlamı kurmaya zorlar ve Chrome bunu
       * "Blocked script execution in 'about:blank'…" ile reddeder — kaydırma
       * SESSİZCE düşerdi ve uzun sayfalarda kayıt donmuş görünürdü.
       * `scrollingElement` (quirks modda `body`, standartta `documentElement`)
       * belgenin KENDİ alanıdır; pencereye hiç dokunmadan aynı işi yapar.
       */
      const scroller = doc.scrollingElement ?? doc.documentElement;
      if (scroller !== null) {
        scroller.scrollLeft = x;
        scroller.scrollTop = y;
      }
      break;
    }

    case 'mouse': {
      const x = num(ev['x']);
      const y = num(ev['y']);
      if (x !== null && y !== null) hooks.onPointer(x, y);
      break;
    }

    case 'click': {
      const x = num(ev['x']);
      const y = num(ev['y']);
      if (x !== null && y !== null) {
        hooks.onPointer(x, y);
        hooks.onClick(x, y);
      }
      break;
    }

    case 'resize': {
      const w = num(ev['w']);
      const h = num(ev['h']);
      if (w !== null && h !== null && w > 0 && h > 0) hooks.onViewport(w, h);
      break;
    }

    case 'page': {
      const path = str(ev['path']);
      if (path !== null) hooks.onPage(path);
      break;
    }

    default:
      break; // bilinmeyen olay tipi → sessizce atla (ileri sürüm uyumu)
  }
}

/* -------------------------------------------------------------------------- */
/* İşaretçi görünümü                                                            */
/* -------------------------------------------------------------------------- */

/** §35.5 — "2xx nötr, 4xx turuncu, 5xx kırmızı". */
const NET_TONE_CLASS: Record<NetRow['tone'], string> = {
  ok: 'text-[#6b7280]',
  warn: 'text-[#c2410c]',
  error: 'text-[#b91c1c] font-semibold',
};

/** §22.7 — "js_error kırmızı, rage turuncu, sayfa değişimi dikey çizgi". */
const MARKER_STYLE: Record<MarkerKind, { className: string; label: string }> = {
  js_error: { className: 'h-4 w-[3px] rounded-sm bg-red-500', label: 'JS hatası' },
  rage: { className: 'h-4 w-[3px] rounded-sm bg-brand', label: 'Rage / dead click' },
  page: { className: 'h-5 w-px bg-[#9ca3af]', label: 'Sayfa değişimi' },
};

/* -------------------------------------------------------------------------- */
/* Bileşen                                                                      */
/* -------------------------------------------------------------------------- */

export default function ReplayPlayer({
  pid,
  sid,
  sessionEvents,
  baseOrigin,
  onClose,
}: ReplayPlayerProps) {
  const baseHref = baseOrigin ?? null;

  /* --- veri (§22.7 iki uç: gövdesiz liste + chunk gövdeleri) --------------- */

  const list = useQuery({
    queryKey: qk.sessionReplay(pid, sid),
    queryFn: ({ signal }) => getSessionReplay(pid, sid, signal),
  });

  const chunks = useMemo(() => list.data?.chunks ?? [], [list.data]);
  const seqKey = chunks.map((c) => c.seq).join(',');

  /*
   * §33.4 — kayıt oturum bayt tavanı nedeniyle KESİLDİ mi (§22.3)?
   *
   * Bayrağı yalnız SON chunk'ta aramıyoruz: chunk'lar kayıp-toleranslıdır (§22.5),
   * kesik chunk'ın gövdesi düşmüş ama meta satırı durmuş olabilir. Herhangi bir
   * chunk kesikse kayıt eksiktir ve kullanıcı bunu BİLMELİDİR.
   */
  const truncated = chunks.some((c) => c.truncated === 1);

  const bodies = useQuery({
    queryKey: [...qk.sessionReplay(pid, sid), 'bodies', seqKey],
    enabled: chunks.length > 0,
    // Kayıt değişmez (geçmiş oturum) — yeniden odaklanmada tekrar indirmeye gerek yok.
    staleTime: Number.POSITIVE_INFINITY,
    queryFn: async ({ signal }): Promise<(ReplayEventLike[] | null)[]> => {
      /*
       * Chunk sayısı azdır (§22.5 hedef 64KB/chunk), paralel çekmek ilk kareyi hızlandırır.
       * Tek bir chunk düşerse kayıt tamamen kaybolmamalı (§22.5 kayıp-tolerans): o chunk
       * null geçilir, motor onu eksene almaz. Hepsi düşerse hata durumu gösterilir.
       */
      const results = await Promise.all(
        chunks.map((chunk) =>
          getReplayChunk(pid, sid, chunk.seq, signal)
            .then((body) => normalizeChunkBody(body))
            .catch(() => null),
        ),
      );
      if (results.every((body) => body === null)) throw new Error('Kayıt gövdeleri alınamadı.');
      return results;
    },
  });

  /* --- zaman ekseni (saf motor) ------------------------------------------- */

  const timeline = useMemo(
    () => buildTimeline(chunks, bodies.data ?? []),
    [chunks, bodies.data],
  );
  const playback = useMemo(() => compressGaps(timeline.events), [timeline]);
  const duration = playback.duration;

  const markerList = useMemo(
    () => buildMarkers(sessionEvents ?? [], timeline.startAt, timeline.endAt),
    [sessionEvents, timeline],
  );

  /** §10 olay listesi — oynatma eksenine taşınmış hâli (sağ panel senkronu). */
  const syncedEvents = useMemo(
    () =>
      (sessionEvents ?? []).map((event, index) => ({
        index,
        event,
        playAt: mapToPlayTime(playback.events, event.ts),
      })),
    [sessionEvents, playback],
  );

  /* --- oynatma durumu ------------------------------------------------------ */

  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState<Speed>(1);
  const [cursor, setCursor] = useState(0);
  const [viewport, setViewport] = useState(DEFAULT_VIEWPORT);
  const [stage, setStage] = useState({ w: 0, h: 0 });
  const [path, setPath] = useState<string | null>(null);
  const [frameError, setFrameError] = useState<string | null>(null);

  const frameRef = useRef<HTMLIFrameElement | null>(null);
  const stageRef = useRef<HTMLDivElement | null>(null);
  const pointerRef = useRef<HTMLDivElement | null>(null);
  const rippleRef = useRef<HTMLDivElement | null>(null);
  const rippleTimer = useRef<number | null>(null);
  const listRef = useRef<HTMLOListElement | null>(null);

  const nodesRef = useRef<NodeMap>(new Map());
  /** Uygulanacak SIRADAKİ olayın dizini — ileri sarma buradan devam eder. */
  const appliedRef = useRef(0);
  /** En son uygulanan oynatma zamanı — geri sarma tespiti için. */
  const appliedAtRef = useRef(0);
  const cursorRef = useRef(0);

  const hooks = useMemo<StageHooks>(
    () => ({
      onViewport: (w, h) =>
        setViewport((prev) => (prev.w === w && prev.h === h ? prev : { w, h })),
      onPage: (next) => setPath((prev) => (prev === next ? prev : next)),
      onPointer: (x, y) => {
        // İmleç her karede değişir; state yerine doğrudan stil yazıyoruz (render fırtınası yok).
        const el = pointerRef.current;
        if (el === null) return;
        el.style.transform = `translate(${x}px, ${y}px)`;
        el.style.opacity = '1';
      },
      onClick: (x, y) => {
        const el = rippleRef.current;
        if (el === null) return;
        el.style.transform = `translate(${x}px, ${y}px) scale(1)`;
        el.style.opacity = '0.75';
        if (rippleTimer.current !== null) window.clearTimeout(rippleTimer.current);
        rippleTimer.current = window.setTimeout(() => {
          el.style.transform = `translate(${x}px, ${y}px) scale(2.2)`;
          el.style.opacity = '0';
        }, 30);
      },
    }),
    [],
  );

  useEffect(
    () => () => {
      if (rippleTimer.current !== null) window.clearTimeout(rippleTimer.current);
    },
    [],
  );

  /** Sahneyi boşaltır: DOM, kimlik haritası ve uygulama imleci başa döner. */
  const resetStage = useCallback(() => {
    nodesRef.current.clear();
    appliedRef.current = 0;
    appliedAtRef.current = 0;
    const doc = frameRef.current?.contentDocument ?? null;
    if (doc !== null) clearDocument(doc);
  }, []);

  /** `target` oynatma zamanına kadarki olayları uygular (yalnız İLERİ yön). */
  const applyUpTo = useCallback(
    (target: number) => {
      const doc = frameRef.current?.contentDocument ?? null;
      if (doc === null) return;
      const events = playback.events;
      while (appliedRef.current < events.length && events[appliedRef.current].playAt <= target) {
        applyEvent(doc, events[appliedRef.current].ev, nodesRef.current, hooks, baseHref);
        appliedRef.current += 1;
      }
      appliedAtRef.current = target;
    },
    [playback, hooks, baseHref],
  );

  /**
   * §22.7 — timeline'a tıklama/geri sarma. Mutation akışı tek yönlüdür (ters mutation
   * kaydedilmez), bu yüzden geri sarmada sahne baştan kurulup hedefe kadar ileri sarılır.
   */
  const seek = useCallback(
    (target: number) => {
      const next = clamp(target, 0, duration);
      if (next < appliedAtRef.current) resetStage();
      applyUpTo(next);
      cursorRef.current = next;
      setCursor(next);
    },
    [duration, applyUpTo, resetStage],
  );

  /* --- yeni kayıt yüklendiğinde sahneyi kur ------------------------------- */

  useEffect(() => {
    if (playback.events.length === 0) return;
    if ((frameRef.current?.contentDocument ?? null) === null) {
      // Çerçeveye erişilemiyorsa (sandbox politikası) sessiz boş ekran yerine açık uyarı.
      setFrameError('Kayıt çerçevesine erişilemedi.');
      return;
    }
    setFrameError(null);
    resetStage();
    cursorRef.current = 0;
    setCursor(0);
    applyUpTo(0); // playAt=0 olan ilk snapshot hemen görünsün
    setPlaying(true);
  }, [playback, resetStage, applyUpTo]);

  /* --- oynatma döngüsü (§22.7 hız 1×/2×/4×) ------------------------------- */

  useEffect(() => {
    if (!playing || duration <= 0) return;
    let last = performance.now();
    let raf = 0;

    const tick = (now: number): void => {
      const delta = (now - last) * speed;
      last = now;
      const next = cursorRef.current + delta;
      if (next >= duration) {
        seek(duration);
        setPlaying(false);
        return;
      }
      seek(next);
      raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [playing, speed, duration, seek]);

  /* --- sahne ölçeği -------------------------------------------------------- */

  useEffect(() => {
    const el = stageRef.current;
    if (el === null || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver((entries) => {
      const rect = entries[0]?.contentRect;
      if (rect === undefined) return;
      setStage({ w: rect.width, h: rect.height });
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [list.isSuccess, bodies.isSuccess]);

  // Kayıt viewport'u panelin alanına sığdırılır; büyütme yapılmaz (1× tavan) —
  // yukarı ölçeklenen DOM bulanıklaşır ve piksel konumlu imleç yanıltır.
  const scale =
    stage.w > 0 && stage.h > 0
      ? Math.min(1, stage.w / viewport.w, stage.h / viewport.h)
      : 1;

  /* --- sağ panel senkronu -------------------------------------------------- */

  /** §35.5 — ağ satırları oynatma ekseninde (sıkıştırılmış zaman) hesaplanır. */
  const netList = useMemo(() => buildNetRows(playback.events), [playback]);

  /*
   * §35.5 — "ağ sekmesi, oturumda hiç `net` olayı yoksa GÖRÜNMEZ".
   * Sekme kaybolduğunda seçim üzerinde kalırsa panel boş görünürdü; bu yüzden
   * seçim türetilir, saklanan durum yalnız kullanıcı NİYETİDİR.
   */
  const [wantsNet, setWantsNet] = useState(false);
  const netVisible = netList.length > 0;
  const showNet = wantsNet && netVisible;

  const activeNetIndex = useMemo(() => {
    let index = -1;
    for (let i = 0; i < netList.length; i += 1) {
      if (netList[i]!.playAt <= cursor) index = i;
      else break;
    }
    return index;
  }, [netList, cursor]);

  const activeEventIndex = useMemo(() => {
    let index = -1;
    for (const item of syncedEvents) {
      if (item.playAt <= cursor) index = item.index;
      else break;
    }
    return index;
  }, [syncedEvents, cursor]);

  useEffect(() => {
    const container = listRef.current;
    if (container === null || activeEventIndex < 0) return;
    const active = container.querySelector(`[data-event-index="${activeEventIndex}"]`);
    active?.scrollIntoView({ block: 'nearest' });
  }, [activeEventIndex]);

  // §35.5 — ağ sekmesi de oynatmayla senkron kayar (aynı `listRef`; tek liste render edilir).
  useEffect(() => {
    const container = listRef.current;
    if (container === null || !showNet || activeNetIndex < 0) return;
    container.querySelector(`[data-net-index="${activeNetIndex}"]`)?.scrollIntoView({
      block: 'nearest',
    });
  }, [activeNetIndex, showNet]);

  /* --- kontroller ---------------------------------------------------------- */

  const progressPct = duration > 0 ? (cursor / duration) * 100 : 0;

  function handleTrackPointer(event: React.MouseEvent<HTMLDivElement>): void {
    const rect = event.currentTarget.getBoundingClientRect();
    if (rect.width <= 0) return;
    seek(((event.clientX - rect.left) / rect.width) * duration);
  }

  function handleTrackKey(event: React.KeyboardEvent<HTMLDivElement>): void {
    // Klavye ile de sarılabilmeli (§13 erişilebilirlik dili): oklar 5 sn, Home/End uçlar.
    const step = 5_000;
    if (event.key === 'ArrowRight') seek(cursorRef.current + step);
    else if (event.key === 'ArrowLeft') seek(cursorRef.current - step);
    else if (event.key === 'Home') seek(0);
    else if (event.key === 'End') seek(duration);
    else if (event.key === ' ' || event.key === 'Enter') setPlaying((prev) => !prev);
    else return;
    event.preventDefault();
  }

  /* --- gövde --------------------------------------------------------------- */

  const loading = list.isLoading || (chunks.length > 0 && bodies.isLoading);
  const failed = list.isError || bodies.isError;
  const empty = !loading && !failed && playback.events.length === 0;

  let body: JSX.Element;
  if (failed) {
    const error = (list.error ?? bodies.error) as Error | null;
    body = (
      <div className="p-5">
        <ErrorState
          title="Kayıt alınamadı"
          description={error?.message}
          onRetry={() => {
            void list.refetch();
            void bodies.refetch();
          }}
        />
      </div>
    );
  } else if (loading) {
    body = <LoadingBlock label="Kayıt yükleniyor…" />;
  } else if (empty) {
    body = (
      <div className="p-5">
        <EmptyState
          compact
          icon={<Film className="h-6 w-6 text-[#9ca3af]" />}
          title="Bu oturumun kaydı yok"
          description="Kayıt yalnızca örneklenen ya da hata/rage gibi bir tetikle yüklenen oturumlarda tutulur (§22.3). Bu oturum için kaydedilmiş DOM verisi bulunmuyor."
        />
      </div>
    );
  } else {
    body = (
      <div className="flex min-h-0 flex-1 gap-4 p-4">
        {/* sahne + kontroller */}
        <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-3">
          <div
            ref={stageRef}
            data-testid="replay-sahne"
            className="relative min-h-[240px] flex-1 overflow-hidden rounded-xl border border-[#e5e7eb] bg-[#f3f4f6]"
          >
            {frameError !== null ? (
              <p className="absolute inset-0 flex items-center justify-center px-6 text-center text-[13px] text-[#6b7280]">
                {frameError}
              </p>
            ) : null}

            <div
              className="absolute left-0 top-0 origin-top-left"
              style={{ width: viewport.w, height: viewport.h, transform: `scale(${scale})` }}
            >
              {/*
                §22.7 KARARI — kaydedilen sitenin JS'i panelde ASLA çalışmaz: `allow-scripts`
                VERİLMEZ, dolayısıyla çerçeve içinde script/inline handler çalışmaz (XSS
                yüzeyi sıfır). `allow-same-origin` ise zorunludur: tamamen boş `sandbox=""`
                çerçeveyi opak origin'e alır ve `contentDocument` null döner — snapshot'ı DOM'a
                kurup mutation'ları düğüm kimliğiyle uygulamak imkânsız hâle gelirdi (srcDoc
                ile kurmak da mutation'ları uygulanamaz kılardı).
              */}
              <iframe
                ref={frameRef}
                title="Oturum kaydı"
                data-testid="replay-iframe"
                sandbox="allow-same-origin"
                className="h-full w-full border-0 bg-white"
                onLoad={() => {
                  // Çerçeve (yeniden) yüklendiyse sahne uçtu — mevcut imleçten geri kur.
                  resetStage();
                  applyUpTo(cursorRef.current);
                }}
              />

              {/* imleç ve tık halkası — kayıt DOM'una dokunmadan üstte çizilir */}
              <div
                ref={rippleRef}
                aria-hidden="true"
                className="pointer-events-none absolute left-0 top-0 -ml-3 -mt-3 h-6 w-6 rounded-full bg-brand opacity-0 transition-all duration-300"
              />
              <div
                ref={pointerRef}
                aria-hidden="true"
                className="pointer-events-none absolute left-0 top-0 -ml-1.5 -mt-1.5 h-3 w-3 rounded-full border-2 border-white bg-[#111827] opacity-0 shadow"
              />
            </div>
          </div>

          {/* zaman çizelgesi + işaretçiler */}
          <div
            role="slider"
            tabIndex={0}
            aria-label="Kayıt zaman çizelgesi"
            aria-valuemin={0}
            aria-valuemax={Math.round(duration)}
            aria-valuenow={Math.round(cursor)}
            aria-valuetext={`${formatClock(cursor)} / ${formatClock(duration)}`}
            data-testid="replay-timeline"
            onClick={handleTrackPointer}
            onKeyDown={handleTrackKey}
            className="relative h-7 w-full cursor-pointer select-none"
          >
            <div className="absolute inset-x-0 top-1/2 h-1.5 -translate-y-1/2 rounded-full bg-[#e5e7eb]" />
            <div
              className="absolute left-0 top-1/2 h-1.5 -translate-y-1/2 rounded-full bg-brand"
              style={{ width: `${progressPct}%` }}
            />

            {markerList.map((marker, index) => {
              // İşaretçiler MUTLAK eksende üretilir; çubuk sıkıştırılmış eksendedir (§22.7).
              const playAt = mapToPlayTime(playback.events, marker.at);
              const left = duration > 0 ? (playAt / duration) * 100 : marker.pct;
              const style = MARKER_STYLE[marker.kind];
              return (
                <button
                  key={`${marker.at}-${marker.kind}-${index}`}
                  type="button"
                  title={style.label}
                  aria-label={`${style.label} işaretçisi`}
                  data-testid="replay-isaretci"
                  data-kind={marker.kind}
                  onClick={(event) => {
                    event.stopPropagation();
                    seek(playAt);
                  }}
                  className="absolute top-1/2 flex -translate-x-1/2 -translate-y-1/2 items-center justify-center px-1"
                  style={{ left: `${left}%` }}
                >
                  <span aria-hidden="true" className={style.className} />
                </button>
              );
            })}

            <div
              aria-hidden="true"
              className="absolute top-1/2 h-3.5 w-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white bg-brand shadow"
              style={{ left: `${progressPct}%` }}
            />
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              data-testid="replay-oynat"
              className="btn-primary w-[104px] justify-center"
              onClick={() => {
                // Sona gelmişken oynat'a basmak baştan başlatır — ayrı bir düğme gerekmesin.
                if (cursorRef.current >= duration) seek(0);
                setPlaying((prev) => !prev);
              }}
            >
              {playing ? (
                <>
                  <Pause className="h-4 w-4" /> Duraklat
                </>
              ) : (
                <>
                  <Play className="h-4 w-4" /> Oynat
                </>
              )}
            </button>

            <button
              type="button"
              className="btn-ghost"
              aria-label="Başa sar"
              onClick={() => seek(0)}
            >
              <RotateCcw className="h-4 w-4" />
            </button>

            <span className="num text-[12px] text-[#6b7280]">
              {formatClock(cursor)} / {formatClock(duration)}
            </span>

            {playback.skippedMs > 0 ? (
              <span className="text-[12px] text-[#9ca3af]">
                · {Math.round(playback.skippedMs / 1000)} sn inaktivite atlandı
              </span>
            ) : null}

            {path !== null ? (
              <span className="truncate font-mono text-[11px] text-[#9ca3af]">{path}</span>
            ) : null}

            <div className="ml-auto flex items-center gap-1" role="group" aria-label="Oynatma hızı">
              {SPEEDS.map((value) => (
                <button
                  key={value}
                  type="button"
                  aria-pressed={speed === value}
                  onClick={() => setSpeed(value)}
                  className={`h-8 rounded-lg px-2.5 text-[12px] transition-colors ${
                    speed === value
                      ? 'bg-[#fff4ec] font-medium text-brand'
                      : 'text-[#6b7280] hover:bg-[#f3f4f6] hover:text-[#111827]'
                  }`}
                >
                  {value}×
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* §22.7 / §35.5 — sağ panel: oturum olayları + (varsa) ağ sekmesi */}
        {syncedEvents.length > 0 || netVisible ? (
          <aside
            className="hidden w-64 shrink-0 flex-col rounded-xl border border-[#e5e7eb] lg:flex"
            data-testid="replay-side-panel"
          >
            {netVisible ? (
              /*
               * §35.5 — sekme şeridi YALNIZCA ağ olayı varsa çizilir. Tek sekmeli bir
               * şerit kullanıcıya seçenek varmış izlenimi verir ve boş sekmeye götürür.
               */
              <div
                role="tablist"
                aria-label="Kayıt paneli"
                className="flex border-b border-[#e5e7eb]"
              >
                {[
                  { id: 'events', label: 'Olaylar', on: !showNet },
                  { id: 'net', label: `Ağ (${netList.length})`, on: showNet },
                ].map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    role="tab"
                    aria-selected={tab.on}
                    data-testid={`replay-tab-${tab.id}`}
                    onClick={() => setWantsNet(tab.id === 'net')}
                    className={`flex-1 px-3 py-2 text-[12px] font-semibold transition-colors ${
                      tab.on
                        ? 'border-b-2 border-brand text-brand'
                        : 'text-[#6b7280] hover:text-[#111827]'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            ) : (
              <h3 className="border-b border-[#e5e7eb] px-3 py-2 text-[12px] font-semibold text-[#374151]">
                Oturum olayları
              </h3>
            )}

            {showNet ? (
              <ol
                ref={listRef}
                className="min-h-0 flex-1 overflow-y-auto p-1.5"
                data-testid="replay-net-list"
              >
                {netList.map((row, index) => (
                  <li key={`${row.playAt}-${index}`} data-net-index={index}>
                    <button
                      type="button"
                      onClick={() => seek(row.playAt)}
                      data-testid="replay-net-row"
                      data-tone={row.tone}
                      title={`${row.method} ${row.path}`}
                      className={`w-full rounded px-2 py-1 text-left transition-colors ${
                        index === activeNetIndex ? 'bg-[#fff4ec]' : 'hover:bg-[#fafafa]'
                      }`}
                    >
                      <span className="flex items-center gap-1.5">
                        <span className="num w-10 shrink-0 text-right font-mono text-[11px] text-[#9ca3af]">
                          {formatClock(row.playAt)}
                        </span>
                        <span className={`shrink-0 font-mono text-[11px] ${NET_TONE_CLASS[row.tone]}`}>
                          {row.status === 0 ? '—' : row.status}
                        </span>
                        <span className="min-w-0 flex-1 truncate text-[12px] text-[#374151]">
                          <span className="text-[#9ca3af]">{row.method}</span> {row.path}
                        </span>
                        <span
                          className={`num shrink-0 font-mono text-[11px] text-[#6b7280] ${
                            row.slow ? 'font-bold text-[#b45309]' : ''
                          }`}
                        >
                          {row.ms}ms
                        </span>
                      </span>
                    </button>
                  </li>
                ))}
              </ol>
            ) : (
              <ol ref={listRef} className="min-h-0 flex-1 overflow-y-auto p-1.5">
                {syncedEvents.map((item) => {
                  const active = item.index === activeEventIndex;
                  return (
                    <li key={item.index} data-event-index={item.index}>
                      <button
                        type="button"
                        onClick={() => seek(item.playAt)}
                        className={`flex w-full items-center gap-2 rounded px-2 py-1 text-left transition-colors ${
                          active ? 'bg-[#fff4ec]' : 'hover:bg-[#fafafa]'
                        }`}
                      >
                        <span className="num w-10 shrink-0 text-right font-mono text-[11px] text-[#9ca3af]">
                          {formatClock(item.playAt)}
                        </span>
                        <span
                          className={`min-w-0 flex-1 truncate text-[12px] ${
                            active ? 'font-medium text-brand' : 'text-[#374151]'
                          }`}
                        >
                          {item.event.type}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ol>
            )}
          </aside>
        ) : null}
      </div>
    );
  }

  /* --- kabuk --------------------------------------------------------------- */

  return (
    <div className="flex min-h-0 flex-1 flex-col" data-testid="replay-player">
      {/* Başlık şeridi Card'ın başlık dilini taklit eder — çağıran zaten kart kabuğunu çizer. */}
      <header className="flex flex-wrap items-start justify-between gap-3 border-b border-[#e5e7eb] px-5 py-3.5">
        <div className="min-w-0">
          <h2 className="flex items-center gap-2 text-[14px] font-semibold text-[#111827]">
            <span className="truncate">Oturum kaydı</span>
            {/*
              §33.4 — sessiz kırpma YASAKTIR: oynatıcının erken bitmesi, açıklanmadığında
              "replay bozuk" olarak okunur. Rozet, eksikliği KAYDIN KENDİSİ kadar görünür kılar.
            */}
            {truncated ? (
              <span
                data-testid="replay-truncated-badge"
                title="Oturum kaydı §22.3 bayt tavanına ulaştığı için burada kesildi; sonrası kaydedilmedi."
                className="shrink-0 rounded-md border border-[#fde68a] bg-[#fffbeb] px-2 py-0.5 text-[11px] font-medium text-[#92400e]"
              >
                Kayıt kesildi
              </span>
            ) : null}
          </h2>
          <p className="mt-0.5 truncate text-[12px] text-[#9ca3af]">
            <span className="font-mono">{sid.slice(0, 12)}</span>
            {chunks.length > 0 ? ` · ${chunks.length} parça` : ''}
            {duration > 0 ? ` · ${formatClock(duration)}` : ''}
            {truncated ? ' · bütçe nedeniyle eksik' : ''}
          </p>
        </div>
        <button type="button" className="btn-ghost" onClick={onClose}>
          <X className="h-4 w-4" /> Kapat
        </button>
      </header>

      {body}
    </div>
  );
}
